import React, { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, Tag, Activity, Image as ImageIcon, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../../Context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { formatCurrency } from '../../Utils/formatters';
import VehicleAvailabilityModal from '../../Components/Admin/VehicleAvailabilityModal';
import { API_URL } from '../../config';

const AdminVehicles = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('Tümü');
  const [selectedVehicleForCalendar, setSelectedVehicleForCalendar] = useState(null);
  const { token } = useAuth();
  const navigate = useNavigate();

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchVehicles = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/admin/vehicles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setVehicles(data);
      }
    } catch (err) {
      console.error('Araçlar yüklenemedi:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Reset page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filterType]);

  const handleOpenModal = (vehicle = null) => {
    if (vehicle) {
      navigate(`/admin/vehicles/edit/${vehicle.id}`);
    } else {
      navigate('/admin/vehicles/add');
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Emin misiniz?',
      text: "Bu aracı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#334155',
      confirmButtonText: 'Evet, Sil!',
      cancelButtonText: 'İptal',
      background: '#0f172a',
      color: '#fff'
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(`${API_URL}/admin/vehicles/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          fetchVehicles();
          Swal.fire('Silindi!', 'Araç başarıyla silindi.', 'success');
        }
      } catch (err) {
        console.error('Silme hatası:', err);
      }
    }
  };

  // Pagination Logic
  const filteredVehicles = vehicles.filter(car => filterType === 'Tümü' || car.type === filterType);
  const totalPages = Math.ceil(filteredVehicles.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentVehicles = filteredVehicles.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="admin-vehicles">
      <div className="admin-header-flex" style={{ display: 'flex', flexDirection: window.innerWidth < 576 ? 'column' : 'row', justifyContent: 'space-between', alignItems: window.innerWidth < 576 ? 'flex-start' : 'center', marginBottom: '2rem', gap: '1rem' }}>
        <div>
          <h1 className="admin-card-title" style={{ fontSize: 'clamp(1.5rem, 4vw, 2rem)', marginBottom: '0.25rem' }}>Araç Yönetimi</h1>
          <p style={{ color: 'var(--text-dim)', fontSize: '0.9rem' }}>Filonuzdaki tüm araçları buradan yönetebilirsiniz. ({filteredVehicles.length} Araç)</p>
        </div>
        
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', background: 'rgba(255,255,255,0.05)', padding: '0.25rem', borderRadius: '0.75rem' }}>
            {['Tümü', 'Kiralık', 'Satılık'].map(type => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                style={{
                  background: filterType === type ? 'var(--primary)' : 'transparent',
                  color: filterType === type ? '#fff' : 'var(--text-dim)',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '0.85rem',
                  transition: 'all 0.2s'
                }}
              >
                {type}
              </button>
            ))}
          </div>

          <button 
            className="btn btn-primary" 
            onClick={() => handleOpenModal()}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.25rem', borderRadius: '0.75rem', fontWeight: '700', fontSize: '0.9rem' }}
          >
            <Plus size={18} /> Yeni Araç Ekle
          </button>
        </div>
      </div>

      <div className="admin-card" style={{ borderRadius: '1.25rem', border: '1px solid var(--navbar-border)', padding: 0, overflow: 'hidden', marginBottom: '2rem' }}>
        <div className="admin-table-wrapper">
          <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: 'rgba(255,255,255,0.02)' }}>
              <tr>
                <th style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap' }}>Görsel</th>
                <th style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap' }}>Araç / Plaka</th>
                <th style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap' }}>Kategori</th>
                <th style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap' }}>Tür</th>
                <th style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap' }}>Fiyat</th>
                <th style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap' }}>Durum</th>
                <th style={{ padding: '1rem 1.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {currentVehicles.map((car) => (
                <tr key={car.id} style={{ borderBottom: '1px solid var(--navbar-border)' }}>
                  <td style={{ padding: '1rem 1.5rem' }}>
                    {car.images && car.images[0] ? (
                      <img src={car.images[0]} alt={car.name} style={{ width: '60px', height: '40px', objectFit: 'cover', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.1)' }} />
                    ) : (
                      <div style={{ width: '60px', height: '40px', background: '#1a1a1a', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <ImageIcon size={18} color="#334155" />
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '1rem 1.5rem' }}>
                    <div style={{ whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: '700', fontSize: '0.95rem' }}>{car.name}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{car.brand} {car.model}</span>
                        {car.license_plate && (
                          <span style={{ 
                            fontSize: '0.65rem', 
                            background: '#1a1a1a', 
                            color: 'white', 
                            padding: '1px 6px', 
                            borderRadius: '3px',
                            fontWeight: '900',
                            border: '1px solid #003399',
                            marginLeft: '4px'
                          }}>
                            {car.license_plate}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '1rem 1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                      <Tag size={12} color="var(--text-dim)" /> {car.category}
                    </div>
                  </td>
                  <td style={{ padding: '1rem 1.5rem' }}>
                    <span style={{ 
                      padding: '0.3rem 0.8rem', 
                      borderRadius: '2rem', 
                      fontSize: '0.65rem', 
                      background: car.type === 'Satılık' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                      color: car.type === 'Satılık' ? '#4ade80' : '#60a5fa',
                      border: `1px solid ${car.type === 'Satılık' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(59, 130, 246, 0.2)'}`,
                      fontWeight: '700',
                      whiteSpace: 'nowrap'
                    }}>
                      {car.type}
                    </span>
                  </td>
                  <td style={{ padding: '1rem 1.5rem', fontWeight: '800', color: '#00d1ff', whiteSpace: 'nowrap' }}>{formatCurrency(car.price)}</td>
                  <td style={{ padding: '1rem 1.5rem' }}>
                    <span className={`badge ${car.status === 'Aktif' || car.status === 'Yayında' ? 'badge-success' : 'badge-warning'}`} style={{ whiteSpace: 'nowrap' }}>
                      <Activity size={10} style={{ marginRight: '4px' }} />
                      {car.status}
                    </span>
                  </td>
                  <td style={{ padding: '1rem 1.5rem', textAlign: 'right' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                      <button onClick={() => setSelectedVehicleForCalendar(car)} style={{ background: 'rgba(0, 209, 255, 0.05)', border: 'none', color: 'var(--primary)', padding: '0.4rem', borderRadius: '0.5rem', cursor: 'pointer' }} title="Müsaitlik Takvimi">
                        <Calendar size={16} />
                      </button>
                      <button onClick={() => handleOpenModal(car)} style={{ background: 'rgba(255,255,255,0.03)', border: 'none', color: 'white', padding: '0.4rem', borderRadius: '0.5rem', cursor: 'pointer' }} title="Düzenle">
                        <Edit2 size={16} />
                      </button>
                      <button onClick={() => handleDelete(car.id)} style={{ background: 'rgba(239, 68, 68, 0.05)', border: 'none', color: '#f87171', padding: '0.4rem', borderRadius: '0.5rem', cursor: 'pointer' }} title="Sil">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {currentVehicles.length === 0 && (
                <tr>
                  <td colSpan="7" style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-dim)' }}>
                    {loading ? 'Yükleniyor...' : 'Araba bulunamadı.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.6rem', marginTop: '2rem', paddingBottom: '3rem', flexWrap: 'wrap' }}>
          <button 
            disabled={currentPage === 1}
            onClick={() => paginate(currentPage - 1)}
            style={{ 
              background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', 
              color: currentPage === 1 ? '#475569' : 'var(--primary)', 
              width: '42px', height: '42px', borderRadius: '0.75rem', 
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer', fontWeight: '700', 
              display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s'
            }}
          >
            <ChevronLeft size={20} />
          </button>
          
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i + 1}
                onClick={() => paginate(i + 1)}
                style={{
                  width: '42px', height: '42px', borderRadius: '0.75rem', border: '1px solid',
                  borderColor: currentPage === i + 1 ? 'var(--primary)' : 'rgba(255,255,255,0.1)',
                  background: currentPage === i + 1 ? 'var(--primary)' : 'rgba(255,255,255,0.02)',
                  color: currentPage === i + 1 ? '#000' : 'white',
                  fontWeight: '800', cursor: 'pointer', transition: 'all 0.2s',
                  boxShadow: currentPage === i + 1 ? '0 0 15px rgba(0, 209, 255, 0.3)' : 'none',
                  transform: currentPage === i + 1 ? 'scale(1.05)' : 'scale(1)'
                }}
              >
                {i + 1}
              </button>
            ))}
          </div>

          <button 
            disabled={currentPage === totalPages}
            onClick={() => paginate(currentPage + 1)}
            style={{ 
              background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', 
              color: currentPage === totalPages ? '#475569' : 'var(--primary)', 
              width: '42px', height: '42px', borderRadius: '0.75rem', 
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', fontWeight: '700',
              display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s'
            }}
          >
            <ChevronRight size={20} />
          </button>
        </div>
      )}

      {selectedVehicleForCalendar && (
        <VehicleAvailabilityModal 
          vehicle={selectedVehicleForCalendar} 
          token={token} 
          onClose={() => setSelectedVehicleForCalendar(null)} 
          fetchVehicles={fetchVehicles}
        />
      )}
    </div>
  );
};

export default AdminVehicles;
